import type { Plan, PlanLimits } from "./plan";

export type AccountPlan = {
  plan: Plan;
  planName: string;
  isPro: boolean;
  limits: PlanLimits;
  usage: {
    savedPortfolios: number;
    aiMixesThisMonth: number;
  };
  usageMonth: string;
  currentPeriodEnd: number | null;
};

type BillingErrorPayload = {
  error?: string;
  code?: string;
  limit?: number;
  upgradePath?: string | null;
};

export class BillingApiError extends Error {
  code?: string;
  limit?: number;
  upgradePath?: string | null;

  constructor(message: string, payload: BillingErrorPayload = {}) {
    super(message);
    this.name = "BillingApiError";
    this.code = payload.code;
    this.limit = payload.limit;
    this.upgradePath = payload.upgradePath;
  }
}

async function parseError(response: Response, fallback: string) {
  const payload = await response.json().catch(() => null) as BillingErrorPayload | null;
  return new BillingApiError(payload?.error ?? fallback, payload ?? {});
}

export async function loadAccountPlan(): Promise<AccountPlan> {
  const response = await fetch("/api/account/plan");
  if (!response.ok) throw await parseError(response, "Your plan could not be loaded.");
  const payload = await response.json().catch(() => null) as AccountPlan | null;
  if (!payload || (payload.plan !== "free" && payload.plan !== "pro")) throw new Error("Your plan could not be loaded.");
  return payload;
}

export async function startBillingCheckout(interval: "monthly" | "annual"): Promise<{ url: string }> {
  const response = await fetch("/api/billing/checkout", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ interval }) });
  if (!response.ok) throw await parseError(response, "Replay Pro checkout is unavailable.");
  const payload = await response.json().catch(() => null) as { url?: unknown } | null;
  if (!payload || typeof payload.url !== "string") throw new Error("Replay Pro checkout is unavailable.");
  return { url: payload.url };
}

export async function openBillingPortal(): Promise<{ url: string }> {
  const response = await fetch("/api/billing/portal", { method: "POST" });
  if (!response.ok) throw await parseError(response, "Billing management is unavailable.");
  const payload = await response.json().catch(() => null) as { url?: unknown } | null;
  if (!payload || typeof payload.url !== "string") throw new Error("Billing management is unavailable.");
  return { url: payload.url };
}
